import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { getStaticFileUrl } from "../utils/config";
import { useNavigate } from "react-router-dom"; // useNavigate 임포트

interface BusinessAreaItem {
  id: string;
  title: string;
  description: string;
  iconUrl: string;
}

interface BusinessAreaData {
  mainTitle: string;
  mainDescription: string;
  items: BusinessAreaItem[];
}

const BusinessAreaSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate(); // navigate 함수 생성
  
  const [businessAreaData, setBusinessAreaData] = useState<BusinessAreaData>({
    mainTitle: 'ABOUT US',
    mainDescription: '클라이언트가 원하는 요구사항을 정확하고 신속하게 제공합니다.',
    items: [
      {
        id: '1',
        title: 'BRAND STRATEGY',
        description: '브랜드 특성에 맞는 다양한 형태의\n캠페인 전략 수립',
        iconUrl: getStaticFileUrl('/img/home/icon_circle1.png'),
      },
      {
        id: '2',
        title: 'CONTENTS MARKETING',
        description: 'SNS기획 - 운영 - 제작 등\nSNS 마케팅',
        iconUrl: getStaticFileUrl('/img/home/icon_circle2.png'),
      },
      {
        id: '3',
        title: 'PERFORMANCE MARKETING',
        description: 'UX,UI /웹사이트/ \n앱&솔루션 개발 / 운영지원',
        iconUrl: getStaticFileUrl('/img/home/icon_circle3.png'),
      },
    ],
  });

  useEffect(() => {
    // API에서 데이터 불러오기
    const loadBusinessAreaData = async () => {
      try {
        const { contentApi } = await import('../services/api');
        const data = await contentApi.get('business-area');
        if (data && Array.isArray(data) && data.length > 0) {
          // API 데이터가 있으면 사용
          const processedData = {
            mainTitle: data[0]?.title || businessAreaData.mainTitle,
            mainDescription: data[0]?.content || businessAreaData.mainDescription,
            items: data.slice(1).map((item: any) => ({
              id: item.id,
              title: item.title,
              description: item.content,
              iconUrl: item.imageUrl?.startsWith('http') ? item.imageUrl : getStaticFileUrl(item.imageUrl || '/img/home/icon_circle1.png')
            })) || businessAreaData.items
          };
          setBusinessAreaData(processedData);
        }
      } catch (error) {
        console.error('Business Area 데이터 로드 실패:', error);
      }
    };

    loadBusinessAreaData();
  }, []);

  return (
    <Box
      sx={{
        width: "100vw",
        height: "auto", // 모바일에서 잘리지 않도록 변경
        padding: isMobile ? "4rem 0" : "6.25rem 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 배경 이미지 */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${getStaticFileUrl('/img/home/business.png')})`,
          backgroundSize: "cover",
          zIndex: 1,
        }}
      />

      {/* 어두운 배경 오버레이 */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 2,
        }}
      />

      {/* 내용물 */}
      <Box
        className="auto"
        sx={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          color: "#fff",
          padding: isMobile ? "2rem" : "0",
        }}
      >
        <Box
          sx={{
            marginTop: isMobile ? "1rem" : "1.5rem",
            textAlign: "center",
            marginBottom: isMobile ? "1rem" : "3.125rem",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: isMobile ? "1.8rem" : "2.5rem",
              fontWeight: "700",
              marginBottom: theme.spacing(2),
            }}
          >
            {businessAreaData.mainTitle}
          </Typography>
          <Typography
            component="p"
            sx={{
              fontSize: isMobile ? "0.9rem" : "1.15rem",
            }}
          >
            {businessAreaData.mainDescription}
          </Typography>
        </Box>

        {/* 비즈니스 영역 목록 */}
        <Box
          component="ul"
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "center",
            listStyle: "none",
            padding: 0,
            gap: isMobile ? theme.spacing(3) : theme.spacing(6),
          }}
        >
          {businessAreaData.items.map((item, index) => (
            <Box
              component="li"
              key={index}
              sx={{
                flex: 1,
                textAlign: "center",
              }}
            >
              <Box
                className="circle"
                sx={{
                  position: "relative",
                  width: isMobile ? "8rem" : "18rem",
                  height: isMobile ? "8rem" : "18rem",
                  border: "0.1875rem solid #d9d9d9",
                  borderRadius: "50%",
                  margin: isMobile ? "0 auto 0.8rem" : "0 auto 1.5rem",
                }}
              >
                <Box
                  className="inner"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) scale(1.2)",
                  }}
                >
                  <Box
                    className="icon"
                    sx={{
                      display: "block",
                      height: isMobile ? "1.2rem" : "2rem",
                      backgroundImage: `url(${item.iconUrl})`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "50% 50%",
                      backgroundSize: "contain",
                      marginBottom: "0.3rem",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: isMobile ? "0.7rem" : "1rem",
                      fontWeight: "600",
                    }}
                  >
                    {item.title.split(" ").map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: isMobile ? "0.7rem" : "1rem",
                  lineHeight: "1.6",
                  whiteSpace: "pre-line",
                }}
              >
                {item.description}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 버튼 */}
        <Box
          className="btns"
          sx={{
            marginTop: "4rem", // 기존 8rem → 줄여서 버튼이 짤리지 않도록
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.spacing(2),
          }}
        >
          <Box
            onClick={() => {
              navigate("/about");
              window.scrollTo({ top: 0, behavior: "smooth" }); // 페이지 상단으로 스크롤 이동
            }}
            sx={{
              textDecoration: "none",
              padding: theme.spacing(1, 3),
              color: "#fff",
              border: "0.1rem solid #fff",
              borderRadius: "1.875rem",
              fontSize: isMobile ? "0.7rem" : "1rem",
              fontWeight: "400",
              textAlign: "center",
              transition: "background-color 0.3s, border-color 0.3s",
              ":hover": {
                backgroundColor: "#f7941e",
                borderColor: "#f7941e",
                color: "#fff",
              },
              width: "auto", // 텍스트 크기에 맞춰 너비 조절
              display: "inline-block", // 전체 너비 차지하지 않도록 처리
              maxWidth: "fit-content", // 내용에 맞게 최대 너비 제한
              cursor: "pointer", // 클릭 가능함을 나타내기 위한 커서 변경
            }}
          >
            자세히 알아보기
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BusinessAreaSection;
